import type { Api } from '@client/adManager/Api/index.js';
import { getAdvtComponentElementFromSlot } from '@client/core/utils/dom/domUtils.js';
import { debug } from '@schibsted-nmp/advertising-shared';

import { CreativeMessageEvent } from './CreativeMessageEvent.js';

export namespace GridSizingHandler {
  export type Data = CreativeMessageEvent.Data.Value<
    CreativeMessageEvent.Type.SetGridSizing,
    {
      adUnit: string;
      size: Size | undefined;
    }
  >;

  export enum Size {
    Default = 'DEFAULT',
    Cell = 'CELL',
    Row = 'ROW'
  }

  export enum CssClass {
    Cell = 'advt-grid--cell',
    Row = 'advt-grid--row'
  }

  export function initiate(googletag: Api.Loaded) {
    googletag.pubads().addEventListener('slotRequested', cleanup);

    window.addEventListener('message', (event: CreativeMessageEvent) => {
      // called from wallpaper creative to initate wallpaper
      // this will listen on all global post messages
      // but we want to filter it down to -> allowed origins and setGridSizing type
      if (!CreativeMessageEvent.isGoogleSafeFrameOrigin(event.origin)) return;
      if (event.data.type !== CreativeMessageEvent.Type.SetGridSizing) return;

      try {
        const { adUnit, size } = event.data;

        // find GAM slot by adUnit:
        const slot = googletag
          .pubads()
          .getSlots()
          .find((slot) => slot.getAdUnitPath() === adUnit);

        if (!slot) {
          throw new Error(`GAM slot not found for adUnit: ${adUnit}`);
        }

        const advtComponentElement = getAdvtComponentElementFromSlot(slot);

        if (!advtComponentElement) {
          throw new Error(
            `advt-component not found for slot element id: ${slot.getSlotElementId()}`
          );
        }

        const cssClassKey = getCssClassKey(size);
        advtComponentElement.classList.add(CssClass[cssClassKey]);
        debug.message`Handled setGridSizing message for adUnit ${adUnit} using size ${cssClassKey}`().verbose();
      } catch (error) {
        debug.message`Failed handling setGridSizing message`(error).error();
      }
    });
  }

  export function getCssClassKey(
    size: Size | undefined
  ): keyof typeof CssClass {
    switch (size) {
      case undefined:
      case Size.Default:
      case Size.Cell:
        return 'Cell';
      case Size.Row:
        return 'Row';
      default:
        throw new Error(`Unknown size value: ${String(size)}`);
    }
  }

  export function cleanup({ slot }: { slot: googletag.Slot }) {
    return getAdvtComponentElementFromSlot(slot)?.classList.remove(
      ...Object.values(CssClass)
    );
  }
}
